import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/hooks/use-toast';
import {
  FlaskConical,
  Search,
  Check,
  Loader2,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  DollarSign,
  Clock,
  MapPin,
  Stethoscope,
  CreditCard,
  Mail,
} from 'lucide-react';
import {
  createOrder, getPanels, LabPanel, type CreateLabOrderRequest,
} from '@/services/labService';
import { emrService } from '@/services/emrService';
import { useClinic } from '@/contexts/ClinicContext';
import { DatePicker } from '@/components/ui/date-picker';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { api } from '@/services/api';
import { format, parse, parseISO, isValid as isValidDate } from 'date-fns';
import { PhoneField } from '@/components/booking/PhoneField';
import { patientService } from '@/services/patientService';
import { usStates } from '@/components/ui/state-input';
import AddressInput from '@/components/ui/AddressInput';
import { cacheKeys } from '@/lib/cacheKeys';

interface LabOrderSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  /** Optional. Falls back to fetched patient.full_name when omitted. */
  patientName?: string;
  patientEmail?: string;
  patientPhone?: string;
  patientAddress?: {
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  patientDob?: string;
  patientGender?: string;
  onOrderCreated?: () => void;
  onPatientUpdated?: () => void;
  guidedOrderId?: string;
  /** Pre-select a panel (e.g. from the Available Panels tab) */
  preSelectedPanel?: LabPanel | null;
  /**
   * In-visit context. When set, the order is anchored to this encounter
   * and the active-encounter banner is suppressed.
   */
  encounterId?: string | null;
  /**
   * Pre-visit context (no encounter yet). When set without encounterId,
   * `off_encounter_reason` defaults to 'pre_visit_lab' instead of
   * 'external_order' and the order is linked to this appointment.
   */
  appointmentId?: string | null;
  /**
   * Override the off_encounter_reason fallback. Only consulted when
   * neither encounterId nor appointmentId is set.
   */
  offEncounterReason?: CreateLabOrderRequest['off_encounter_reason'];
}

type Step = 'patient-details' | 'tests' | 'confirm' | 'payment';

/**
 * What the clinic chose on the Payment step.
 *  - 'pay-now' : collect the card in the sheet via Stripe Elements.
 *  - 'skip'    : create the order and let the backend email a pay link;
 *                patient can also pay from the portal.
 * 'idle' is the initial state before the clinic picks.
 */
type PaymentMode = 'idle' | 'pay-now' | 'skip';

const LabOrderSheet: React.FC<LabOrderSheetProps> = ({
  open,
  onOpenChange,
  patientId,
  patientName,
  patientEmail,
  patientPhone,
  patientAddress,
  patientDob,
  patientGender,
  onOrderCreated,
  onPatientUpdated,
  guidedOrderId,
  preSelectedPanel,
  encounterId,
  appointmentId,
  offEncounterReason,
}) => {
  const { clinic } = useClinic();
  const qc = useQueryClient();

  // Track whether the user has explicitly opted to attach a pending
  // off-encounter order to the patient's active visit (banner action).
  // Holds the encounter id chosen via the banner; null means "use the
  // explicitly-passed encounterId / fall back to off_encounter_reason."
  const [linkToEncounterId, setLinkToEncounterId] = useState<string | null>(null);

  // The encounter id the order will actually be stamped with.
  const effectiveEncounterId = encounterId || linkToEncounterId || null;

  // Look up the patient's currently in-progress encounter, but only
  // when the caller didn't already pass one. Pre-visit (appointmentId)
  // and explicit-link cases stay opt-in; we don't want to surprise the
  // provider by silently retargeting the order.
  const { data: activeEncounter } = useQuery({
    queryKey: cacheKeys.patientActiveEncounter(patientId),
    queryFn: () => emrService.getActiveEncounterForPatient(clinic!.id, patientId),
    enabled: open && !!clinic?.id && !!patientId && !encounterId && !appointmentId,
  });
  const showLinkBanner = !!activeEncounter && !linkToEncounterId;

  useEffect(() => {
    if (!open) {
      setLinkToEncounterId(null);
    }
  }, [open]);

  // Auto-fetch full patient record when caller didn't pass enough
  // detail. Cockpit / standalone Lab Orders pass everything; the
  // pre-visit AppointmentSheet only knows patient_id, and asking the
  // provider to retype demographics that are already in the DB is
  // pointless friction.
  const propsAreIncomplete
    = !patientEmail || !patientPhone || !patientDob
    || !patientAddress?.state || !patientAddress?.zip;
  const { data: fetchedPatient } = useQuery({
    queryKey: ['patient', patientId, 'detail-for-lab-sheet'],
    queryFn: () => patientService.getPatientById(patientId),
    enabled: open && !!patientId && propsAreIncomplete,
  });
  const fetchedAddr = (() => {
    const addrs = (fetchedPatient as any)?.addresses;
    if (!Array.isArray(addrs)) return null;
    return addrs.find((a: any) => a.address_type === 'shipping')
      || addrs.find((a: any) => a.address_type === 'home')
      || addrs[0]
      || null;
  })();
  const resolvedPatientPhone = patientPhone
    || (fetchedPatient as any)?.phone
    || (fetchedPatient as any)?.contacts?.find((c: any) => c.key === 'phone')?.value
    || '';
  const resolvedPatientEmail = patientEmail
    || (fetchedPatient as any)?.email
    || (fetchedPatient as any)?.contacts?.find((c: any) => c.key === 'email')?.value
    || '';
  const resolvedPatientDob = patientDob || (fetchedPatient as any)?.dob || '';
  const resolvedPatientGender = patientGender
    || (fetchedPatient as any)?.biological_sex
    || (fetchedPatient as any)?.gender
    || 'unknown';
  const resolvedPatientName = patientName
    || (fetchedPatient as any)?.full_name
    || [
      (fetchedPatient as any)?.first_name,
      (fetchedPatient as any)?.last_name,
    ].filter(Boolean).join(' ')
    || 'patient';
  const resolvedPatientAddress = (patientAddress?.state && patientAddress?.zip)
    ? patientAddress
    : fetchedAddr
      ? {
        street: fetchedAddr.line_1 || '',
        city: fetchedAddr.city || '',
        state: fetchedAddr.stateInfo?.code || fetchedAddr.state_code || '',
        zip: fetchedAddr.postal_code || '',
      }
      : patientAddress;

  const [step, setStep] = useState<Step>('tests');

  // Panel selection
  const [selectedPanel, setSelectedPanel] = useState<LabPanel | null>(null);
  const [panels, setPanels] = useState<LabPanel[]>([]);
  const [loadingPanels, setLoadingPanels] = useState(false);
  const [panelSearch, setPanelSearch] = useState('');
  const [expandedPanelId, setExpandedPanelId] = useState<string | null>(null);

  // Collection method derived from selected panel
  const collectionMethod = useMemo(() => {
    if (!selectedPanel) return 'walk_in';
    const method = selectedPanel.collectionMethod || selectedPanel.collection_method;
    if (method === 'walk_in_test') return 'walk_in';
    if (method === 'at_home_phlebotomy') return 'at_home_phlebotomy';
    return 'walk_in';
  }, [selectedPanel]);

  // Order submission
  const [submitting, setSubmitting] = useState(false);

  // Payment step state. The clinic picks between "Pay now" (inline
  // Stripe Elements on the confirm step) and "Skip" (email pay link).
  //
  // Lifecycle for pay-now:
  //   payment step  → chooser only, paymentMode='pay-now'
  //   Continue      → pre-create order with notifyPatient=false and
  //                   mint a PaymentIntent via /intent; result → intentData
  //   confirm step  → <Elements> + <PaymentElement> mount with the
  //                   clientSecret; "Pay $X" inside Elements runs
  //                   stripe.confirmPayment. Webhook handles Junction.
  //
  // Lifecycle for skip:
  //   payment step  → chooser only, paymentMode='skip'
  //   Continue      → advance to confirm (no server call yet)
  //   confirm step  → "Create Order" creates with skipPayment=true;
  //                   backend fires the pay-link email.
  const [paymentMode, setPaymentMode] = useState<PaymentMode>('idle');
  const [preparingPayment, setPreparingPayment] = useState(false);
  const [intentData, setIntentData] = useState<{
    clientSecret: string;
    stripePublishableKey: string;
    stripeConnectedAccountId: string;
    // Customer Session client secret — required for saved-card display.
    // May be null if the backend couldn't resolve a Customer.
    customerSessionClientSecret: string | null;
    orderId: string;
    /** Invoice payment_page_token (the unified pay flow). */
    payToken: string;
    amount: number | null;
  } | null>(null);

  // Patient details editing state
  const [editablePatientData, setEditablePatientData] = useState({
    phone: resolvedPatientPhone,
    email: resolvedPatientEmail,
    dob: resolvedPatientDob,
    address: {
      street: resolvedPatientAddress?.street || '',
      city: resolvedPatientAddress?.city || '',
      state: resolvedPatientAddress?.state || '',
      zip: resolvedPatientAddress?.zip || '',
    },
  });
  const [savingPatientData, setSavingPatientData] = useState(false);
  const [hasPatientDetailsStep, setHasPatientDetailsStep] = useState(false);

  // Sync editable data whenever the resolved values change (props OR
  // fetched patient hydrated). useState only seeds once, so without
  // this, a self-fetch hit would never reach the form.
  useEffect(() => {
    setEditablePatientData({
      phone: resolvedPatientPhone,
      email: resolvedPatientEmail,
      dob: resolvedPatientDob,
      address: {
        street: resolvedPatientAddress?.street || '',
        city: resolvedPatientAddress?.city || '',
        state: resolvedPatientAddress?.state || '',
        zip: resolvedPatientAddress?.zip || '',
      },
    });
  }, [
    resolvedPatientPhone, resolvedPatientEmail, resolvedPatientDob,
    resolvedPatientAddress?.street, resolvedPatientAddress?.city,
    resolvedPatientAddress?.state, resolvedPatientAddress?.zip,
  ]);

  // Fetch panels
  const fetchPanels = useCallback(async () => {
    try {
      setLoadingPanels(true);
      const data = await getPanels();
      setPanels(data);
    } catch (error) {
      console.error('Error fetching panels:', error);
    } finally {
      setLoadingPanels(false);
    }
  }, []);

  // Track previous open state
  const prevOpenRef = useRef(false);

  // Reset state when sheet opens.
  // Routes the user into the patient-details step ONLY when the
  // resolved values (props OR fetched record) are still incomplete.
  // We re-evaluate when those values change so a self-fetch that
  // hydrates after the open transition can fast-forward to the
  // tests step instead of wasting the user's time.
  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;

    if (justOpened) {
      setSelectedPanel(preSelectedPanel || null);
      setPanelSearch('');
      setExpandedPanelId(null);
      fetchPanels();
    }

    if (open) {
      // Clear payment state from any previous session. Radix Sheet
      // keeps children mounted across open/close so React state
      // survives — without this, a second order on the same sheet
      // reuses a terminal PaymentIntent and <Elements> crashes with
      // "PaymentIntent is in a terminal state".
      setPaymentMode('idle');
      setIntentData(null);
      setPreparingPayment(false);

      const missingFields = [];
      if (!resolvedPatientPhone) missingFields.push('phone');
      if (!resolvedPatientEmail) missingFields.push('email');
      if (!resolvedPatientDob) missingFields.push('date of birth');
      if (!resolvedPatientAddress?.state || !resolvedPatientAddress?.zip) missingFields.push('address');

      if (missingFields.length > 0) {
        // Only force the step if we haven't moved past it already
        setHasPatientDetailsStep(true);
        if (justOpened) setStep('patient-details');
      } else {
        setHasPatientDetailsStep(false);
        if (justOpened) setStep('tests');
      }
    }
  }, [
    open, resolvedPatientPhone, resolvedPatientEmail, resolvedPatientDob,
    resolvedPatientAddress?.state, resolvedPatientAddress?.zip,
    preSelectedPanel, fetchPanels,
  ]);

  // Filter panels by search
  const filteredPanels = useMemo(() => {
    if (!panelSearch.trim()) return panels;
    const lower = panelSearch.toLowerCase();
    return panels.filter(
      p =>
        p.name.toLowerCase().includes(lower) ||
        p.description?.toLowerCase().includes(lower),
    );
  }, [panels, panelSearch]);

  const handleSavePatientDetails = async () => {
    try {
      setSavingPatientData(true);

      if (
        !editablePatientData.phone ||
        !editablePatientData.email ||
        !editablePatientData.dob ||
        !editablePatientData.address.state ||
        !editablePatientData.address.zip
      ) {
        toast({ title: 'Incomplete Information', description: 'Please fill in all required fields', variant: 'destructive' });
        setSavingPatientData(false);
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(editablePatientData.email)) {
        toast({ title: 'Invalid Email', description: 'Please enter a valid email address', variant: 'destructive' });
        setSavingPatientData(false);
        return;
      }

      const zipRegex = /^\d{5}$/;
      if (!zipRegex.test(editablePatientData.address.zip)) {
        toast({ title: 'Invalid ZIP Code', description: 'Please enter a valid 5-digit ZIP code', variant: 'destructive' });
        setSavingPatientData(false);
        return;
      }

      await patientService.updatePatient(patientId, {
        contacts: [
          { key: 'phone', value: editablePatientData.phone },
          { key: 'email', value: editablePatientData.email },
        ],
        dob: editablePatientData.dob,
        addresses: [
          {
            addressLine1: editablePatientData.address.street,
            city: editablePatientData.address.city,
            state: editablePatientData.address.state,
            postalCode: editablePatientData.address.zip,
            address_type: 'shipping',
          },
        ],
      });

      toast({ title: 'Patient Details Updated', description: 'Patient information has been saved successfully', variant: 'success' });
      setStep('tests');
      onPatientUpdated?.();
    } catch (error) {
      toast({ title: 'Update Failed', description: error instanceof Error ? error.message : 'Failed to update patient details', variant: 'destructive' });
    } finally {
      setSavingPatientData(false);
    }
  };

  /**
   * Build the lab-order payload from the current wizard state.
   * Returns `null` (with a toast) when required fields are missing so
   * the caller can bail out. This used to be the monolithic submit
   * handler — it's now split so Place Order can dispatch on paymentMode.
   */
  const buildOrderData = async () => {
    if (!selectedPanel) return null;

    if (!clinic?.id) {
      toast({ title: 'Clinic Required', description: 'Please select a clinic first', variant: 'destructive' });
      return;
    }

    try {
      setSubmitting(true);

      const nameParts = (resolvedPatientName || '').split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const finalPhone = resolvedPatientPhone || editablePatientData.phone;
      const finalEmail = resolvedPatientEmail || editablePatientData.email;
      const finalDob = resolvedPatientDob || editablePatientData.dob;
      const finalAddress
        = resolvedPatientAddress?.state && resolvedPatientAddress?.zip
          ? resolvedPatientAddress
          : editablePatientData.address;

      if (!finalPhone || !finalEmail || !finalDob) {
        toast({ title: 'Missing Patient Information', description: 'Patient phone, email, and date of birth are required for lab orders', variant: 'destructive' });
        setSubmitting(false);
        return;
      }

      if (!finalAddress?.state || !finalAddress?.zip) {
        toast({ title: 'Missing Address Information', description: 'Patient state and zip code are required for lab orders', variant: 'destructive' });
        setSubmitting(false);
        return;
      }

      const orderData: CreateLabOrderRequest = {
        patientId,
        clinicId: clinic.id,
        collectionMethod,
        panelId: selectedPanel.id,
        // panelName is not part of CreateLabOrderRequest typedef but the
        // backend accepts it as a display label. Cast to any when adding.
        patientDetails: {
          firstName,
          lastName,
          dob: finalDob,
          gender: (resolvedPatientGender as 'male' | 'female' | 'unknown') || 'unknown',
          phone: finalPhone,
          email: finalEmail,
        },
        patientAddress: {
          firstLine: finalAddress.street || '',
          city: finalAddress.city || '',
          state: finalAddress.state,
          zip: finalAddress.zip || '',
        },
        notifyPatient: true,
      };
      (orderData as any).panelName = selectedPanel.name;

      if (guidedOrderId) {
        orderData.guidedOrderId = guidedOrderId;
      }

      // Encounter linkage. Order of precedence:
      //   1. Explicit encounterId prop (in-visit cockpit)
      //   2. linkToEncounterId from the active-visit banner
      //   3. appointmentId + 'pre_visit_lab' reason (AppointmentSheet)
      //   4. caller-provided offEncounterReason
      //   5. labService default ('external_order')
      if (effectiveEncounterId) {
        orderData.encounter_id = effectiveEncounterId;
      } else if (appointmentId) {
        orderData.appointment_id = appointmentId;
        orderData.off_encounter_reason = offEncounterReason || 'pre_visit_lab';
      } else if (offEncounterReason) {
        orderData.off_encounter_reason = offEncounterReason;
      }

      return orderData;
    } catch (error: unknown) {
      const errorMessage =
        (error as { message?: string })?.message || 'Failed to build order';
      toast({ title: 'Order Failed', description: errorMessage, variant: 'destructive' });
      setSubmitting(false);
      return null;
    }
  };

  /**
   * Skip-only Place Order handler. The pay-now branch doesn't route
   * through here — it goes through the Elements-backed child component
   * on the Confirm step, which handles order creation implicitly via
   * the pre-minted PaymentIntent.
   */
  const handleSkipAndCreate = async () => {
    const orderData = await buildOrderData();
    if (!orderData) return;
    try {
      orderData.skipPayment = true;
      await createOrder(orderData);
      toast({
        title: 'Order Created',
        description: `Payment link sent. The lab test will be scheduled once paid.`,
        variant: 'success',
      });

      // Refresh the surfaces that read this order. Encounter / appointment
      // panels need a targeted invalidation since they query different
      // cache keys than the patient-orders list.
      qc.invalidateQueries({ queryKey: cacheKeys.patientLabOrders(patientId) });
      if (effectiveEncounterId) {
        qc.invalidateQueries({ queryKey: cacheKeys.encounterLabOrders(effectiveEncounterId) });
      }
      if (appointmentId) {
        qc.invalidateQueries({ queryKey: cacheKeys.appointmentLabOrders(appointmentId) });
      }

      onOpenChange(false);
      onOrderCreated?.();
    } catch (error: unknown) {
      const message =
        (error as { message?: string })?.message ||
        (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ||
        'Failed to create order';
      toast({ title: 'Order Failed', description: message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Pre-create the order (no email) and mint a PaymentIntent so the
   * Confirm step can mount Stripe Elements against a real clientSecret.
   *
   * Called when the clinic clicks Continue on the Payment step with
   * pay-now selected. On success the caller advances to the confirm
   * step. On failure we surface a toast and stay on payment step.
   */
  const prepareInlinePayment = async (): Promise<boolean> => {
    const orderData = await buildOrderData();
    if (!orderData) return false;

    setPreparingPayment(true);
    try {
      orderData.skipPayment = true;
      orderData.notifyPatient = false;

      const createdOrder = (await createOrder(orderData)) as unknown as {
        id: string;
        price_charged?: number | string | null;
      };

      // The order is persisted at this point — payment may still be
      // pending, but the encounter / appointment / patient lab-order
      // panels should already show it (as "Awaiting Payment") so the
      // provider sees their action took effect. Mirrors handleSkipAndCreate.
      qc.invalidateQueries({ queryKey: cacheKeys.patientLabOrders(patientId) });
      if (effectiveEncounterId) {
        qc.invalidateQueries({ queryKey: cacheKeys.encounterLabOrders(effectiveEncounterId) });
      }
      if (appointmentId) {
        qc.invalidateQueries({ queryKey: cacheKeys.appointmentLabOrders(appointmentId) });
      }

      // Unified billing: every lab order auto-issues an invoice, and the
      // unauthenticated patient pay page is served exclusively by the
      // invoice endpoints.
      const invoiceToken = (createdOrder as { invoice?: { payment_page_token?: string } })
        ?.invoice?.payment_page_token;
      if (!invoiceToken) {
        throw new Error('Server did not return an invoice payment token');
      }
      const intentPath = `/patient-portal/invoices/${invoiceToken}/intent`;

      const intentRes = await api.post<{
        success: boolean;
        data?: {
          stripeClientSecret: string;
          stripePublishableKey: string;
          stripeConnectedAccountId: string;
          stripeCustomerSessionClientSecret?: string | null;
        };
        error?: { message?: string };
      }>(intentPath);

      if (!intentRes.success || !intentRes.data) {
        throw new Error(intentRes.error?.message || 'Unable to initialize payment');
      }

      setIntentData({
        clientSecret: intentRes.data.stripeClientSecret,
        stripePublishableKey: intentRes.data.stripePublishableKey,
        stripeConnectedAccountId: intentRes.data.stripeConnectedAccountId,
        customerSessionClientSecret: intentRes.data.stripeCustomerSessionClientSecret ?? null,
        orderId: createdOrder.id,
        payToken: invoiceToken,
        amount: createdOrder.price_charged != null ? Number(createdOrder.price_charged) : null,
      });
      return true;
    } catch (error: unknown) {
      const message =
        (error as { message?: string })?.message ||
        (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ||
        'Failed to start payment';
      toast({ title: 'Payment Setup Failed', description: message, variant: 'destructive' });
      return false;
    } finally {
      setPreparingPayment(false);
      setSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 'patient-details':
        return (
          editablePatientData.phone &&
          editablePatientData.email &&
          editablePatientData.dob &&
          editablePatientData.address.state &&
          editablePatientData.address.zip
        );
      case 'tests':
        return selectedPanel != null;
      case 'payment':
        return paymentMode !== 'idle';
      case 'confirm':
        return true;
      default:
        return false;
    }
  };

  const nextStep = async () => {
    switch (step) {
      case 'patient-details':
        setStep('tests');
        break;
      case 'tests':
        setStep('payment');
        break;
      case 'payment':
        // Pay-now needs a pre-minted PaymentIntent before Elements can
        // mount on the confirm step. Skip just advances.
        if (paymentMode === 'pay-now' && !intentData) {
          const ok = await prepareInlinePayment();
          if (!ok) return;
        }
        setStep('confirm');
        break;
      case 'confirm':
        // Only the skip branch uses this path; pay-now has its own
        // "Pay $X" button inside the Elements tree.
        handleSkipAndCreate();
        break;
    }
  };

  const prevStep = () => {
    switch (step) {
      case 'confirm':
        // Going back past the confirm step invalidates any minted
        // PaymentIntent — it might be succeeded, processing, or just
        // stale relative to the next forward pass. Clearing forces
        // prepareInlinePayment to mint a fresh order + PI next time.
        setIntentData(null);
        setStep('payment');
        break;
      case 'payment':
        setIntentData(null);
        setStep('tests');
        break;
      case 'tests':
        if (hasPatientDetailsStep) setStep('patient-details');
        break;
    }
  };

  const formatCollectionMethod = (method?: string) => {
    if (method === 'walk_in_test' || method === 'walk_in') return 'Walk-in at Quest';
    if (method === 'at_home_phlebotomy') return 'At-Home Phlebotomy';
    return 'Walk-in';
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className='w-full overflow-y-auto p-6 sm:max-w-xl'>
        <SheetHeader className='mb-6'>
          <SheetTitle className='flex items-center gap-2'>
            <FlaskConical className='h-5 w-5 text-primary' />
            Order Lab Work
          </SheetTitle>
          <SheetDescription>Order lab tests for {resolvedPatientName}</SheetDescription>
        </SheetHeader>

        {/* Active-encounter banner.
            Shown when this patient has an in-progress visit and the
            sheet wasn't opened *from* that visit. Linking is opt-in —
            click to attach this order to the visit instead of letting
            it default to off_encounter_reason='external_order'. */}
        {showLinkBanner && activeEncounter && (
          <div className='mb-4 flex items-start justify-between gap-3 rounded-md border border-indigo-200 bg-indigo-50 p-3 text-sm'>
            <div className='flex items-start gap-2'>
              <Stethoscope className='mt-0.5 h-4 w-4 text-indigo-700' />
              <div>
                <div className='font-medium text-indigo-900'>
                  Patient has an active visit
                </div>
                <div className='text-xs text-indigo-700'>
                  {activeEncounter.chief_complaint
                    ? `Chief complaint: ${activeEncounter.chief_complaint}.`
                    : 'In-progress encounter.'}{' '}
                  Link this lab order to it?
                </div>
              </div>
            </div>
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='border-indigo-300 bg-white text-indigo-700 hover:bg-indigo-100'
              onClick={() => setLinkToEncounterId(activeEncounter.id)}
            >
              Link to visit
            </Button>
          </div>
        )}

        {/* Confirmation chip once a visit-link is selected. */}
        {linkToEncounterId && !encounterId && (
          <div className='mb-4 flex items-center justify-between gap-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900'>
            <span>Order will be linked to the active visit.</span>
            <button
              type='button'
              className='text-emerald-800 underline-offset-2 hover:underline'
              onClick={() => setLinkToEncounterId(null)}
            >
              Undo
            </button>
          </div>
        )}

        {/* Progress Steps */}
        <div className='mb-6 flex items-center gap-2'>
          {(() => {
            const allSteps: Step[] = ['patient-details', 'tests', 'payment', 'confirm'];
            const stepsToShow =
              step === 'patient-details' ||
              !resolvedPatientPhone ||
              !resolvedPatientEmail ||
              !resolvedPatientDob ||
              !resolvedPatientAddress?.state ||
              !resolvedPatientAddress?.zip
                ? allSteps
                : ['tests', 'payment', 'confirm'];

            return stepsToShow.map((s, i) => {
              const stepIndex = allSteps.indexOf(s);
              const currentStepIndex = allSteps.indexOf(step);
              const isCompleted = currentStepIndex > stepIndex;
              const isCurrent = step === s;

              return (
                <React.Fragment key={s}>
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
                      isCurrent
                        ? 'bg-primary text-white'
                        : isCompleted
                          ? 'bg-brand-teal text-white'
                          : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {isCompleted ? <Check className='h-4 w-4' /> : stepIndex + 1}
                  </div>
                  {i < stepsToShow.length - 1 && (
                    <div className={`h-1 flex-1 ${isCompleted ? 'bg-brand-teal' : 'bg-gray-200'}`} />
                  )}
                </React.Fragment>
              );
            });
          })()}
        </div>

        {/* Step 0: Patient Details Editor */}
        {step === 'patient-details' && (
          <div className='space-y-4'>
            <div className='mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4'>
              <div className='flex items-start gap-3'>
                <AlertCircle className='mt-0.5 h-5 w-5 text-amber-600' />
                <div>
                  <h4 className='font-medium text-amber-900'>Missing Required Information</h4>
                  <p className='mt-1 text-sm text-amber-700'>
                    Please complete the following required fields before ordering labs
                  </p>
                </div>
              </div>
            </div>

            <h3 className='text-lg font-medium'>Complete Patient Details</h3>

            <div className='space-y-2'>
              <PhoneField
                value={editablePatientData.phone}
                onChange={val => setEditablePatientData(prev => ({ ...prev, phone: val }))}
                label='Phone Number'
                placeholder='(555) 123-4567'
                required={true}
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='patient-email'>
                Email <span className='text-red-500'>*</span>
              </Label>
              <Input
                id='patient-email'
                type='email'
                value={editablePatientData.email}
                onChange={e => setEditablePatientData(prev => ({ ...prev, email: e.target.value }))}
                placeholder='patient@example.com'
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='patient-dob'>
                Date of Birth <span className='text-red-500'>*</span>
              </Label>
              <DatePicker
                value={
                  editablePatientData.dob
                    ? (() => {
                        try {
                          const parsedIso = parseISO(editablePatientData.dob);
                          if (isValidDate(parsedIso)) return parsedIso;
                          const parsed = parse(editablePatientData.dob, 'yyyy-MM-dd', new Date());
                          if (isValidDate(parsed)) return parsed;
                          const parsed2 = parse(editablePatientData.dob, 'MM/dd/yyyy', new Date());
                          return isValidDate(parsed2) ? parsed2 : undefined;
                        } catch {
                          return undefined;
                        }
                      })()
                    : undefined
                }
                onValueChange={date => {
                  if (date) {
                    setEditablePatientData(prev => ({ ...prev, dob: format(date, 'yyyy-MM-dd') }));
                  } else {
                    setEditablePatientData(prev => ({ ...prev, dob: '' }));
                  }
                }}
                placeholder='MM/DD/YYYY'
                maxDate={new Date()}
              />
            </div>

            <div className='space-y-4 pt-2'>
              <Label className='text-base font-medium'>Address</Label>
              <AddressInput
                value={{
                  addressLine1: editablePatientData.address.street,
                  addressLine2: '',
                  city: editablePatientData.address.city,
                  state: editablePatientData.address.state,
                  postalCode: editablePatientData.address.zip,
                }}
                onChange={addressData => {
                  setEditablePatientData(prev => ({
                    ...prev,
                    address: {
                      street: addressData.addressLine1,
                      city: addressData.city,
                      state: addressData.state,
                      zip: addressData.postalCode,
                    },
                  }));
                }}
                states={usStates.map(s => ({ code: s.value, name: s.label }))}
              />
            </div>
          </div>
        )}

        {/* Step 1: Select Panel (inline list) */}
        {step === 'tests' && (
          <div className='space-y-4'>
            <h3 className='text-lg font-medium'>Select Lab Panel</h3>

            {/* Search */}
            <div className='relative'>
              <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
              <Input
                value={panelSearch}
                onChange={e => setPanelSearch(e.target.value)}
                placeholder='Search panels...'
                className='pl-10'
              />
            </div>

            {/* Panel List */}
            <div className='space-y-2 max-h-[400px] overflow-y-auto'>
              {loadingPanels ? (
                <div className='space-y-2'>
                  {[1, 2, 3].map(i => (
                    <Skeleton key={i} className='h-20 w-full' />
                  ))}
                </div>
              ) : filteredPanels.length === 0 ? (
                <Card className='border-dashed'>
                  <CardContent className='py-8 text-center'>
                    <FlaskConical className='mx-auto mb-2 h-10 w-10 text-slate-300' />
                    <p className='text-sm text-slate-500'>
                      {panels.length === 0
                        ? 'No panels available for this clinic'
                        : 'No panels match your search'}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                filteredPanels.map(panel => {
                  const isSelected = selectedPanel?.id === panel.id;
                  const isExpanded = expandedPanelId === panel.id;
                  const markers = panel.markers || [];
                  const price = panel.patientPrice || panel.price;
                  const method = panel.collectionMethod || panel.collection_method;

                  return (
                    <Card
                      key={panel.id}
                      className={`cursor-pointer transition-all ${
                        isSelected
                          ? 'border-primary bg-primary/5 ring-1 ring-primary'
                          : 'hover:border-primary/50'
                      }`}
                      onClick={() => setSelectedPanel(isSelected ? null : panel)}
                    >
                      <CardContent className='p-3'>
                        <div className='flex items-start justify-between'>
                          <div className='flex-1 min-w-0'>
                            <div className='flex items-center gap-2'>
                              {isSelected && <Check className='h-4 w-4 text-primary shrink-0' />}
                              <span className='font-medium text-sm'>{panel.name}</span>
                              {panel.isDefault && (
                                <Badge variant='outline' className='text-[10px]'>Default</Badge>
                              )}
                              {(panel.fastingRequired || panel.fasting_required) && (
                                <Badge variant='outline' className='text-[10px] text-amber-700 border-amber-200'>Fasting</Badge>
                              )}
                            </div>
                            {panel.description && (
                              <p className='text-xs text-muted-foreground mt-1 line-clamp-1'>
                                {panel.description}
                              </p>
                            )}
                            <div className='flex items-center gap-3 mt-1.5 text-xs text-muted-foreground'>
                              {price != null && (
                                <span className='flex items-center gap-0.5 font-medium text-green-700'>
                                  <DollarSign className='h-3 w-3' />
                                  {Number(price).toFixed(2)}
                                </span>
                              )}
                              {markers.length > 0 && (
                                <span className='flex items-center gap-0.5'>
                                  <FlaskConical className='h-3 w-3' />
                                  {markers.length} markers
                                </span>
                              )}
                              {panel.turnaroundDays && (
                                <span className='flex items-center gap-0.5'>
                                  <Clock className='h-3 w-3' />
                                  ~{panel.turnaroundDays}d
                                </span>
                              )}
                              {method && (
                                <span className='flex items-center gap-0.5'>
                                  <MapPin className='h-3 w-3' />
                                  {method === 'walk_in_test' ? 'Walk-in' : 'At Home'}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Expand markers */}
                          {markers.length > 0 && (
                            <Button
                              variant='ghost'
                              size='icon'
                              className='h-7 w-7 shrink-0'
                              onClick={e => {
                                e.stopPropagation();
                                setExpandedPanelId(isExpanded ? null : panel.id);
                              }}
                            >
                              {isExpanded ? <ChevronUp className='h-3.5 w-3.5' /> : <ChevronDown className='h-3.5 w-3.5' />}
                            </Button>
                          )}
                        </div>

                        {/* Expanded markers */}
                        {isExpanded && markers.length > 0 && (
                          <div className='mt-2 pt-2 border-t'>
                            <p className='text-[10px] font-medium text-muted-foreground mb-1'>Included Biomarkers:</p>
                            <div className='flex flex-wrap gap-1'>
                              {markers.map((m, idx) => (
                                <Badge key={idx} variant='secondary' className='text-[10px] font-normal'>
                                  {m.name}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Step 2: Confirm Order */}
        {step === 'confirm' && selectedPanel && (
          <div className='space-y-4'>
            {/* Compact order summary — patient / panel (w/ price) /
                collection / payment mode on one card. Descriptions and
                biomarker pills live on earlier steps; repeating them
                here was pure noise. */}
            <Card>
              <CardHeader>
                <CardTitle className='text-base'>Order Summary</CardTitle>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div>
                  <Label className='text-sm text-gray-500'>Patient</Label>
                  <p className='font-medium'>{resolvedPatientName}</p>
                </div>

                <Separator />

                <div>
                  <Label className='text-sm text-gray-500'>Lab Panel</Label>
                  <p className='font-medium'>{selectedPanel.name}</p>
                  {selectedPanel.description && (
                    <p className='text-sm text-gray-500 mt-0.5'>{selectedPanel.description}</p>
                  )}
                  {(selectedPanel.patientPrice || selectedPanel.price) && (
                    <p className='text-sm font-medium text-green-700 mt-1'>
                      ${Number(selectedPanel.patientPrice || selectedPanel.price).toFixed(2)}
                    </p>
                  )}
                  {/* Markers */}
                  {selectedPanel.markers && selectedPanel.markers.length > 0 && (
                    <div className='mt-2'>
                      <p className='text-xs text-muted-foreground mb-1'>
                        {selectedPanel.markers.length} biomarkers included:
                      </p>
                      <div className='flex flex-wrap gap-1'>
                        {selectedPanel.markers.map((m, idx) => (
                          <Badge key={idx} variant='secondary' className='text-[10px] font-normal'>
                            {m.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <Separator />

                <div>
                  <Label className='text-sm text-gray-500'>Collection Method</Label>
                  <p className='font-medium'>{formatCollectionMethod(collectionMethod)}</p>
                  <p className='mt-1 text-sm text-gray-500'>
                    {collectionMethod === 'walk_in'
                      ? 'Patient can visit any Quest Diagnostics location with their requisition form'
                      : 'A phlebotomist will visit the patient at home for blood draw'}
                  </p>
                </div>

                <Separator />

                <div>
                  <Label className='text-sm text-gray-500'>Payment</Label>
                  <p className='font-medium inline-flex items-center gap-1.5'>
                    {paymentMode === 'pay-now' ? (
                      <>
                        <CreditCard className='w-3.5 h-3.5 text-primary' />
                        Card in person
                      </>
                    ) : (
                      <>
                        <Mail className='w-3.5 h-3.5 text-primary' />
                        Email pay link
                      </>
                    )}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Inline Stripe Elements — only rendered when pay-now was
                selected on the previous step. The clientSecret was
                pre-minted by prepareInlinePayment. */}
            {paymentMode === 'pay-now' && intentData && (
              <InlineLabCheckout
                clientSecret={intentData.clientSecret}
                customerSessionClientSecret={intentData.customerSessionClientSecret}
                publishableKey={intentData.stripePublishableKey}
                connectedAccountId={intentData.stripeConnectedAccountId}
                payToken={intentData.payToken}
                amount={intentData.amount}
                panelName={selectedPanel.name}
                onSucceeded={() => {
                  // Drop the terminal clientSecret before any refetch /
                  // close effect can fire. Without this the Elements
                  // tree briefly re-renders with a succeeded PI and
                  // Stripe throws "PaymentIntent is in a terminal state".
                  setIntentData(null);
                  // Refresh again now that payment cleared so the
                  // "Awaiting Payment" badge picks up its new status.
                  qc.invalidateQueries({ queryKey: cacheKeys.patientLabOrders(patientId) });
                  if (effectiveEncounterId) {
                    qc.invalidateQueries({ queryKey: cacheKeys.encounterLabOrders(effectiveEncounterId) });
                  }
                  if (appointmentId) {
                    qc.invalidateQueries({ queryKey: cacheKeys.appointmentLabOrders(appointmentId) });
                  }
                  onOpenChange(false);
                  toast({
                    title: 'Payment received',
                    description: 'Order submitted to the lab.',
                    variant: 'success',
                  });
                  onOrderCreated?.();
                }}
              />
            )}
          </div>
        )}

        {step === 'payment' && (
          <div className='space-y-3'>
            <h3 className='text-lg font-medium'>Collect payment</h3>
            <PaymentModeCard
              selected={paymentMode === 'pay-now'}
              onSelect={() => {
                // Clear any stale prep from an earlier pass so Continue
                // always triggers a fresh order + PaymentIntent.
                setIntentData(null);
                setPaymentMode('pay-now');
              }}
              icon={<CreditCard className='w-5 h-5 text-primary' />}
              title='Pay now'
              description="Enter the patient's card on the next step."
            />
            <PaymentModeCard
              selected={paymentMode === 'skip'}
              onSelect={() => {
                setIntentData(null);
                setPaymentMode('skip');
              }}
              icon={<Mail className='w-5 h-5 text-primary' />}
              title='Skip — email pay link'
              description='Patient gets a pay link and can also pay from the portal.'
            />
          </div>
        )}

        {/* Navigation Buttons */}
        <div className='mt-8 flex justify-between border-t pt-4'>
          <Button
            variant='outline'
            onClick={
              step === 'patient-details' || step === 'tests' ? () => onOpenChange(false) : prevStep
            }
          >
            {step === 'patient-details' || step === 'tests' ? 'Cancel' : 'Back'}
          </Button>

          {step === 'patient-details' ? (
            <Button
              onClick={handleSavePatientDetails}
              disabled={savingPatientData || !canProceed()}
            >
              {savingPatientData ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  Saving...
                </>
              ) : (
                <>
                  Save & Continue
                  <ChevronRight className='ml-1 h-4 w-4' />
                </>
              )}
            </Button>
          ) : step === 'confirm' && paymentMode === 'skip' ? (
            <Button onClick={handleSkipAndCreate} disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  Creating Order...
                </>
              ) : (
                <>
                  <Check className='mr-2 h-4 w-4' />
                  Create Order
                </>
              )}
            </Button>
          ) : step === 'confirm' && paymentMode === 'pay-now' ? (
            // Pay-now places the order via the Pay button inside the
            // Elements tree. The nav Place Order button is intentionally
            // omitted on this branch.
            <span />
          ) : (
            <Button onClick={nextStep} disabled={!canProceed() || preparingPayment}>
              {preparingPayment ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  Preparing…
                </>
              ) : (
                <>
                  Continue
                  <ChevronRight className='ml-1 h-4 w-4' />
                </>
              )}
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

/**
 * One-line "label : value" row used by the compact Confirm summary.
 * Kept inline because it's only used on this screen.
 */
function Row({
  label,
  value,
  trailing,
}: {
  label: string;
  value: React.ReactNode;
  trailing?: React.ReactNode;
}) {
  return (
    <div className='flex items-baseline justify-between gap-3'>
      <span className='text-muted-foreground text-[12px] uppercase tracking-wider'>{label}</span>
      <span className='font-medium text-right flex-1'>{value}</span>
      {trailing ? <span className='whitespace-nowrap'>{trailing}</span> : null}
    </div>
  );
}

/**
 * Inline Stripe Elements checkout rendered on the Confirm step when
 * pay-now was selected. The PaymentIntent and clientSecret are
 * pre-minted by prepareInlinePayment; this component just mounts
 * Elements and drives confirmPayment.
 *
 * Note on Stripe Connect: loadStripe's second arg (`stripeAccount`)
 * is the connected account on which the PI lives. The direct-charge
 * model requires the client to confirm under that account, not the
 * platform account.
 */
function InlineLabCheckout({
  clientSecret,
  customerSessionClientSecret,
  publishableKey,
  connectedAccountId,
  payToken,
  amount,
  panelName,
  onSucceeded,
}: {
  clientSecret: string;
  customerSessionClientSecret: string | null;
  publishableKey: string;
  connectedAccountId: string;
  payToken: string;
  amount: number | null;
  panelName: string;
  onSucceeded: () => void;
}) {
  const stripePromise = useMemo<Promise<Stripe | null>>(
    () => loadStripe(publishableKey, { stripeAccount: connectedAccountId }),
    [publishableKey, connectedAccountId],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className='text-base'>Card details</CardTitle>
      </CardHeader>
      <CardContent>
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            // Customer Session unlocks saved-card display in
            // PaymentElement. Omitted only when the backend couldn't
            // resolve a Customer (Elements falls back to a blank form).
            ...(customerSessionClientSecret
              ? { customerSessionClientSecret }
              : {}),
            appearance: { theme: 'stripe' },
          }}
        >
          <InlineLabCheckoutForm
            clientSecret={clientSecret}
            payToken={payToken}
            amount={amount}
            panelName={panelName}
            onSucceeded={onSucceeded}
          />
        </Elements>
      </CardContent>
    </Card>
  );
}

function InlineLabCheckoutForm({
  clientSecret,
  payToken,
  amount,
  panelName,
  onSucceeded,
}: {
  clientSecret: string;
  payToken: string;
  amount: number | null;
  panelName: string;
  onSucceeded: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();

  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements || submitting) return;

    setSubmitting(true);
    setLocalError(null);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setLocalError(submitError.message || 'Please check the card details');
      setSubmitting(false);
      return;
    }

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      clientSecret,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });

    if (error) {
      setLocalError(error.message || 'Payment was declined');
      setSubmitting(false);
      return;
    }

    if (paymentIntent?.status !== 'succeeded' && paymentIntent?.status !== 'processing') {
      setLocalError(`Unexpected payment status: ${paymentIntent?.status || 'unknown'}`);
      setSubmitting(false);
      return;
    }

    // Client-confirmed sync. Stripe has the money; tell our backend to
    // move the order to paid + submit to Junction *now*, instead of
    // waiting for the payment_intent.succeeded webhook. Idempotent: if
    // the webhook beat us, the endpoint returns alreadyPaid=true.
    try {
      await api.post(`/patient-portal/invoices/${payToken}/confirm`, {
        paymentIntentId: paymentIntent.id,
      });
    } catch (err) {
      // Non-fatal — the webhook path is still running. Log and
      // continue to the success UI; the submit-retry job will pick up
      // any stragglers. We intentionally DO NOT treat this as an error
      // because the money is already collected.
      console.warn('[LabOrderSheet] /confirm sync failed, relying on webhook:', err);
    }

    // Synchronously hand control back to the parent — it unmounts the
    // Elements tree (by clearing intentData) so we never keep a
    // terminal clientSecret around long enough for a re-render to
    // trip "PaymentIntent is in a terminal state".
    setCompleted(true);
    onSucceeded();
  };

  const amountDisplay = amount != null ? `$${amount.toFixed(2)}` : panelName;

  if (completed) {
    return (
      <div className='rounded-lg border border-emerald-200 bg-emerald-50 p-3 flex items-center gap-2 text-emerald-900'>
        <Check className='w-4 h-4' />
        <span className='text-sm font-medium'>Payment received — placing order…</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className='space-y-3'>
      <PaymentElement options={{ layout: 'tabs' }} />

      {localError && (
        <div className='rounded-lg border border-destructive/20 bg-destructive/5 p-3 text-xs text-destructive'>
          {localError}
        </div>
      )}

      <Button
        type='submit'
        disabled={!stripe || !elements || submitting}
        className='w-full h-11 gap-2'
      >
        {submitting ? (
          <Loader2 className='w-4 h-4 animate-spin' />
        ) : (
          <CreditCard className='w-4 h-4' />
        )}
        Pay {amountDisplay} & Place Order
      </Button>
    </form>
  );
}

/**
 * Selectable card for the Payment step. Kept inline with LabOrderSheet
 * because it's only used here — not worth a separate file yet.
 */
function PaymentModeCard({
  selected,
  onSelect,
  icon,
  title,
  description,
}: {
  selected: boolean;
  onSelect: () => void;
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <button
      type='button'
      onClick={onSelect}
      className={`w-full text-left rounded-xl border p-4 flex items-start gap-4 transition ${
        selected
          ? 'border-primary ring-2 ring-primary/20 bg-primary/5'
          : 'border-border hover:border-primary/60 hover:bg-muted/30'
      }`}
    >
      <div className='w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0'>
        {icon}
      </div>
      <div className='flex-1'>
        <p className='font-medium'>{title}</p>
        <p className='text-sm text-muted-foreground mt-1'>{description}</p>
      </div>
      <div
        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
          selected ? 'border-primary bg-primary' : 'border-gray-300'
        }`}
      >
        {selected && <Check className='w-3 h-3 text-white' />}
      </div>
    </button>
  );
}

export default LabOrderSheet;
